// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "@reach/router";
import { Divider, message, Table } from "antd";
import { useTranslation } from "react-i18next";
import PageTitle from "../../../components/PageTitle";
import PageBody from "../../../components/PageBody";
import PageHeader from "../../../components/PageHeader";
import { AddKeywordButton } from "../add/AddKeywordButton";
import {
  requestDeleteKeyword,
  requestGetKeywords
} from "../../../../state/keyword/KeywordEffects";
import {
  AuthentifiedUser,
  KeywordInfoResponseDTO
} from "../../../../api/backend-openapi";
import { SpinIcon } from "../../../components/SpinIcon";
import { useStore } from "effector-react";
import { KeywordStore } from "../../../../state/keyword/KeywordStore";
import {
  deleteKeyword,
  setKeywords
} from "../../../../state/keyword/KeywordEvents";
import i18n from "i18next";
import { alignRight, orderAscend } from "../../../../utils/Constant";
import { AlphabeticalSorter } from "../../../../utils/AlphabeticalSorter";
import { showConfirm } from "../../../components/ConfirmModal";
import { DeleteOutlined } from "@ant-design/icons";
import TableButton from "../../../components/TableButton";
import { tooltipDateRender } from "../../../components/CustomTooltip";
import { authentifiedUserStore } from "../../../../state/auth/AuthStore";
import { NboAction } from "../../../types/AuthTypes";
import { displayError } from "../../../../utils/displayError";

const KeywordsScreen: React.FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();

  const keywordStore: KeywordInfoResponseDTO[] | null = useStore(KeywordStore);
  const authentifiedUser: AuthentifiedUser | null = useStore(
    authentifiedUserStore
  );
  const canCreate: boolean =
    authentifiedUser?.rights.nboKeyword?.includes(NboAction.nboCreate) || false;
  const canDelete: boolean =
    authentifiedUser?.rights.nboKeyword?.includes(NboAction.nboDelete) || false;

  const [dataHasLoaded, setDataHasLoaded] = useState<boolean>(false);

  const columns = [
    {
      title: i18n.t("keywords.table.text"),
      key: "text",
      dataIndex: "text",
      ellipsis: true,
      defaultSortOrder: orderAscend,
      sorter: AlphabeticalSorter("text")
    },
    {
      title: i18n.t("keywords.table.creation_date"),
      key: "creationDate",
      dataIndex: "creationDate",
      ellipsis: true,
      sorter: AlphabeticalSorter("creationDate"),
      render: tooltipDateRender
    },
    {
      title: i18n.t("keywords.table.modification_date"),
      key: "modificationDate",
      dataIndex: "modificationDate",
      ellipsis: true,
      sorter: AlphabeticalSorter("modificationDate"),
      render: tooltipDateRender
    },
    {
      title: i18n.t("keywords.table.action"),
      key: "action",
      fixed: alignRight,
      width: 115,
      render: (text: string, record: KeywordInfoResponseDTO) => (
        <div className="d-inline-flex">
          <TableButton
            tooltipTitle={i18n.t("keywords.table.tooltip.delete")}
            icon={<DeleteOutlined />}
            buttonClassName="btn-danger m-2"
            onClick={() =>
              showConfirm(
                i18n.t("keywords.table.confirmation.delete", {
                  keyword: record.text
                }),
                () => {
                  requestDeleteKeyword(record);
                }
              )
            }
          />
        </div>
      )
    }
  ];

  useEffect(() => {
    requestGetKeywords();
  }, []);

  useEffect(() => {
    return requestGetKeywords.done.watch(({ result }) => {
      setDataHasLoaded(true);
      if (result.ok && result.data) {
        setKeywords(result.data);
      } else {
        displayError("keywords.not_found");
      }
    });
  });

  useEffect(() => {
    return requestDeleteKeyword.done.watch(({ params, result }) => {
      if (result.ok) {
        deleteKeyword(params.id);
        message.success(t("keywords.global_buttons.delete.success"));
      } else {
        if (result.errorMessage) {
          displayError(result.errorMessage, false);
        } else {
          displayError("keywords.global_buttons.delete.error");
        }
      }
    });
  });

  return (
    <>
      <PageHeader>
        <PageTitle title={t("keywords.title")} />
      </PageHeader>
      <PageBody className="list-body">
        <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
          <div className="flex-grow-1 d-flex align-items-center">
            <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
              <h3 className="text-secondary mb-0">
                {t("keywords.list_title")}
              </h3>
            </Divider>
          </div>
          {canCreate && (
            <div className="d-flex align-items-center divider-sm-transparent">
              <Divider orientation="right" className="mt-2 mt-sm-3 mb-3">
                <AddKeywordButton />
              </Divider>
            </div>
          )}
        </div>

        <Table
          locale={{ emptyText: dataHasLoaded ? "" : " " }}
          loading={{ spinning: !dataHasLoaded, indicator: SpinIcon }}
          columns={
            canDelete
              ? columns
              : columns.filter(
                  col => col.title !== i18n.t("keywords.table.action")
                )
          }
          dataSource={keywordStore}
          showSorterTooltip={false}
          rowKey={record => record.id}
        />
      </PageBody>
    </>
  );
};

export default KeywordsScreen;
